import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  ButtonGroup,
  IconButton,
  Snackbar,
} from '@mui/material';
import {
  IconArrowLeft,
  IconArrowBackUp,
  IconDotsVertical,
  IconHomeShare,
  IconHomeCheck,
  IconExternalLink,
  IconPlayerPlay,
  IconPrinter,
  IconTag,
  IconTrash,
} from '@tabler/icons-react';
import { WorkItem } from '../components/workitems/workItemTypes';
import { WorkItemExpansionPanel } from '../components/workitems/components/WorkItemExpansionPanel';
import { jobStatusKey } from '../components/technician-card/workItemToRow';
import { formatStatusDisplay } from '../components/workitems/utils/workItemUtils';
import { getWorkItemByNumber, updateWorkItemStatus } from '../services/workItemDataService';
import { JobDeleteDialog } from '../components/workitems/JobDeleteDialog';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { printTicket } from '../services/ticketsApi';
import { ActionMenu } from '../components/dropdown';
import { JobReturnsPanel } from '../components/check-in/JobReturnsPanel';
import { CARD_SX, COMPACT_BTN_SX, COMPACT_BTN_GROUP_SX } from '../styles/designSystem';
import { ReleaseDeviceDialog } from '../components/workitems/deviceRelease/ReleaseDeviceDialog';
import { DeviceWithCustomerChip } from '../components/workitems/deviceRelease/DeviceWithCustomerChip';
import { canReleaseDevice, canReturnDevice } from '../utils/deviceRelease';
import { returnWorkItemDevice } from '../services/workItemDataService';
import './TicketDetailPage.css';

const RETURN_ELIGIBLE_STATUSES = new Set(['resolved', 'closed']);

// A reopen only makes sense once the ticket has been finished off — the same
// closed-out states that qualify for a return.
const REOPENABLE_STATUSES = new Set(['resolved', 'closed']);

/** Dynamics title-case status the orchestrator expects (see workItemStatusConfig). */
const IN_PROGRESS_STATUS = 'In Progress';

const REPAIRSHOPR_BASE_URL = 'https://techsolvers.repairshopr.com';

interface UserInfo {
  displayName: string;
  userPrincipalName: string;
  roles: string[];
}

interface TicketDetailPageProps {
  userInfo?: UserInfo | null;
  /**
   * Triggered by the "Create Return" button. The parent (MainLayout) seeds
   * the new Check-In Create page with this work item and switches view.
   * Optional so the legacy URL-based render path keeps compiling if a caller
   * doesn't wire it.
   */
  onCreateReturn?: (workItem: WorkItem) => void;
}

export const TicketDetailPage: React.FC<TicketDetailPageProps> = ({ userInfo, onCreateReturn }) => {
  const location = useLocation();
  const number = location.pathname.match(/^\/ticket\/(\d+)$/)?.[1];
  const navigate = useNavigate();

  const [workItem, setWorkItem] = useState<WorkItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printSnack, setPrintSnack] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

  // "···" overflow menu — mirrors the dashboard job row's kebab (Delete).
  const overflowRef = useRef<HTMLButtonElement>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const currentUser = useCurrentUser();
  // Same roles the API accepts a job delete from.
  const canDeleteJob = !!currentUser?.hasAnyRole('Admin', 'Manager');
  const [isReopening, setIsReopening] = useState(false);

  // Reopen a resolved/closed ticket straight back to "In Progress". The status
  // POST is fire-and-forget (202) — the orchestrator pushes RS + Dataverse and
  // broadcasts workitems:changed — so we update optimistically and revert on
  // failure, mirroring the dashboard's handleStatusChange.
  const handleReopen = async () => {
    if (!workItem || isReopening) return;
    const prevStatus = workItem.status;
    const prevResolvedAt = workItem.resolvedAt;
    setIsReopening(true);
    setWorkItem((prev) =>
      prev ? { ...prev, status: IN_PROGRESS_STATUS, resolvedAt: undefined } : prev,
    );
    try {
      await updateWorkItemStatus(
        workItem.guid,
        workItem.id || workItem.guid,
        IN_PROGRESS_STATUS,
        workItem.type,
      );
      setPrintSnack({ severity: 'success', message: 'Ticket reopened to In Progress' });
    } catch (e) {
      setWorkItem((prev) =>
        prev ? { ...prev, status: prevStatus, resolvedAt: prevResolvedAt } : prev,
      );
      setPrintSnack({
        severity: 'error',
        message: e instanceof Error ? e.message : 'Failed to reopen ticket',
      });
    } finally {
      setIsReopening(false);
    }
  };

  // The ticket no longer exists — return to where the tech came from.
  const handleJobDeleted = () => navigate(-1);

  const handlePrintTicket = async () => {
    if (!workItem) return;
    setIsPrinting(true);
    try {
      await printTicket(Number(workItem.id));
      setPrintSnack({ severity: 'success', message: 'Ticket print queued in RepairShopr' });
    } catch (e) {
      setPrintSnack({
        severity: 'error',
        message: e instanceof Error ? e.message : 'Failed to print ticket',
      });
    } finally {
      setIsPrinting(false);
    }
  };


  useEffect(() => {
    if (!number || !/^\d+$/.test(number)) {
      setError('Invalid ticket number');
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    getWorkItemByNumber(Number(number), controller.signal)
      .then((item) => {
        // Ignore a superseded/aborted request — its resolution (incl. a
        // null "not found") must not overwrite the live request's state.
        if (controller.signal.aborted) return;
        if (item) {
          setWorkItem(item);
        } else {
          setError(`Ticket #${number} not found`);
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setError(err.message);
        }
      })
      .finally(() => {
        // Don't clear loading for an aborted request — its finally still
        // fires, and doing so would flash the "not found" branch
        // (loading=false, workItem=null) while the live request is still
        // in flight (e.g. StrictMode's second mount).
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [number]);

  const handleWorkItemUpdated = (id: string, updates: Partial<WorkItem>) => {
    setWorkItem((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  // Device release (customer takes it home while the job waits for parts).
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [returningDevice, setReturningDevice] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const handleReturnDevice = async () => {
    if (!workItem) return;
    setReturningDevice(true);
    setReleaseError(null);
    try {
      const result = await returnWorkItemDevice(workItem.guid);
      setWorkItem((prev) =>
        prev ? { ...prev, deviceWithCustomer: false, deviceReturnedAt: result.deviceReturnedAt ?? undefined } : prev,
      );
    } catch (e) {
      setReleaseError(e instanceof Error ? e.message : 'Failed to mark device returned');
    } finally {
      setReturningDevice(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          pt: 8,
        }}
      >
        <CircularProgress />
        <Typography sx={{ fontSize: 14, color: '#888' }}>
          Loading ticket…
        </Typography>
      </Box>
    );
  }

  if (error || !workItem) {
    return (
      <Box className="ticket-detail">
        <Box className="ticket-detail__inner">
          <button type="button" className="ticket-detail__back" onClick={() => navigate(-1)}>
            <IconArrowLeft size={16} stroke={1.5} />
            Back
          </button>
          <Alert severity="error">{error || 'Ticket not found'}</Alert>
        </Box>
      </Box>
    );
  }

  const rsTicketUrl = `${REPAIRSHOPR_BASE_URL}/tickets/${workItem.id}`;
  // Colour key for the status pill — the same mapper the dashboard row uses,
  // so a ticket reads the same colour here as it does in the list.
  const statusKey = jobStatusKey(workItem.status);

  const canReopen = REOPENABLE_STATUSES.has((workItem.status || '').toLowerCase());

  return (
    <Box className="ticket-detail">
      <Box className="ticket-detail__inner">
        {/* Back */}
        <button type="button" className="ticket-detail__back" onClick={() => navigate(-1)}>
          <IconArrowLeft size={16} stroke={1.5} />
          Back
        </button>

        {/* Header card */}
        <Box sx={{ ...CARD_SX, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 600, fontSize: 20, color: '#1a1a1a' }}>
              #{workItem.number} {workItem.title}
            </Typography>
            <span className={`ticket-detail__status ticket-detail__status--${statusKey}`}>
              <span
                className={`ticket-detail__status-dot ticket-detail__status-dot--${statusKey}`}
              />
              {formatStatusDisplay(workItem.status ?? 'New')}
            </span>
            <DeviceWithCustomerChip workItem={workItem} />
            {/* RepairShopr actions */}
            <ButtonGroup variant="outlined" size="small" sx={COMPACT_BTN_GROUP_SX}>
              <Button
                onClick={() => window.open(rsTicketUrl, '_blank', 'noopener,noreferrer')}
                startIcon={<IconExternalLink size={16} stroke={1.5} />}
              >
                RS
              </Button>
              <Button
                onClick={handlePrintTicket}
                disabled={isPrinting}
                startIcon={
                  isPrinting
                    ? <CircularProgress size={14} color="inherit" />
                    : <IconPrinter size={16} stroke={1.5} />
                }
              >
                Print
              </Button>
              <Button
                onClick={() =>
                  window.open(
                    `${REPAIRSHOPR_BASE_URL}/tickets/${workItem.id}/print?job_type=label`,
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
                startIcon={<IconTag size={16} stroke={1.5} />}
              >
                Label
              </Button>
            </ButtonGroup>
            {onCreateReturn && RETURN_ELIGIBLE_STATUSES.has((workItem.status || '').toLowerCase()) && (
              <Button
                variant="outlined"
                size="small"
                sx={COMPACT_BTN_SX}
                onClick={() => onCreateReturn(workItem)}
                startIcon={<IconArrowBackUp size={16} stroke={1.5} />}
              >
                Create Return
              </Button>
            )}
            {canReleaseDevice(workItem) && (
              <Button
                variant="outlined"
                size="small"
                sx={COMPACT_BTN_SX}
                onClick={() => setReleaseOpen(true)}
                startIcon={<IconHomeShare size={16} stroke={1.5} />}
              >
                Release Device
              </Button>
            )}
            {canReturnDevice(workItem) && (
              <Button
                variant="outlined"
                size="small"
                sx={COMPACT_BTN_SX}
                onClick={handleReturnDevice}
                disabled={returningDevice}
                startIcon={
                  returningDevice
                    ? <CircularProgress size={14} color="inherit" />
                    : <IconHomeCheck size={16} stroke={1.5} />
                }
              >
                Mark device returned
              </Button>
            )}
            {(canReopen || canDeleteJob) && (
            <IconButton
              ref={overflowRef}
              size="small"
              title="More actions"
              onClick={() => setOverflowOpen((o) => !o)}
              sx={{ color: '#888', '&:hover': { color: '#555' } }}
            >
              <IconDotsVertical size={18} stroke={1.5} />
            </IconButton>
            )}
            {overflowOpen && (
              <ActionMenu
                anchorRef={overflowRef}
                onClose={() => setOverflowOpen(false)}
                options={[
                  ...(canReopen
                    ? [
                        {
                          label: 'Reopen to Progress',
                          description: 'Set the status back to In Progress',
                          Icon: IconPlayerPlay,
                          onSelect: handleReopen,
                        },
                      ]
                    : []),
                  ...(canDeleteJob
                    ? [
                        {
                          label: 'Delete job',
                          Icon: IconTrash,
                          destructive: true,
                          onSelect: () => setDeleteConfirmOpen(true),
                        },
                      ]
                    : []),
                ]}
              />
            )}
          </Box>
          {(workItem.createdAt || workItem.userFullName || workItem.resolvedAt) && (
            <div className="ticket-detail__facts">
              {workItem.createdAt && (
                <div>
                  <div className="ticket-detail__fact-label">Job Created</div>
                  <div className="ticket-detail__fact-value">
                    {new Date(workItem.createdAt).toLocaleString('en-AU', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              )}
              {workItem.userFullName && (
                <div>
                  <div className="ticket-detail__fact-label">Technician</div>
                  <div className="ticket-detail__fact-value">{workItem.userFullName}</div>
                </div>
              )}
              {workItem.resolvedAt && (
                <div>
                  <div className="ticket-detail__fact-label">Resolved</div>
                  <div className="ticket-detail__fact-value">
                    {new Date(workItem.resolvedAt).toLocaleDateString('en-AU', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </Box>

        {/* Returns linked to this work item (only shown when >=1 exists) */}
        <JobReturnsPanel workItemId={workItem.id} />

        {/* Reuse the existing expansion panel for full details. It renders
            its own sections as bordered-but-transparent boxes because on the
            dashboard it sits inside a white accordion row — so it needs a white
            card under it here, or every section shows the grey page surface
            through it. (The V2 panel draws its own surfaces; when this page
            moves to WorkItemPanelContainer, drop the wrapper.) */}
        <Box sx={{ ...CARD_SX, p: 2 }}>
          <WorkItemExpansionPanel
            workItem={workItem}
            userName={userInfo?.displayName ?? ''}
            isAdmin={userInfo?.roles.includes('admin') ?? false}
            onWorkItemUpdated={handleWorkItemUpdated}
          />
        </Box>
      </Box>
      <JobDeleteDialog
        open={deleteConfirmOpen}
        job={workItem}
        onClose={() => setDeleteConfirmOpen(false)}
        onDeleted={handleJobDeleted}
      />
      <ReleaseDeviceDialog
        open={releaseOpen}
        workItem={workItem}
        onClose={() => setReleaseOpen(false)}
        onReleased={(updates) => setWorkItem((prev) => (prev ? { ...prev, ...updates } : prev))}
      />
      <Snackbar
        open={!!releaseError}
        autoHideDuration={6000}
        onClose={() => setReleaseError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {releaseError ? (
          <Alert severity="error" onClose={() => setReleaseError(null)} sx={{ width: '100%' }}>
            {releaseError}
          </Alert>
        ) : undefined}
      </Snackbar>
      <Snackbar
        open={!!printSnack}
        autoHideDuration={4000}
        onClose={() => setPrintSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {printSnack ? (
          <Alert
            severity={printSnack.severity}
            onClose={() => setPrintSnack(null)}
            sx={{ width: '100%' }}
          >
            {printSnack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};
